import { Col, Row, Spinner } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import TableRowAction from 'components/common/TableRowAction';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { useParams } from 'react-router-dom';
import AddHospitalModal from './AddHospitalModal';
import AddReportModal from './AddReportModal';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { supabase } from 'lib/supabase';
import { toast } from 'react-toastify';
import {
  useBeneficiaryHospitals,
  useCreateHospitalOrphan,
  useDeleteHospitalOrphan,
  useMedicalReports,
  useCreateMedicalReport,
  useHospitals
} from 'hooks/useBeneficiaries';
import useUpload from 'hooks/useUpload';
import { useAuthContext } from 'context/authContext';

// Component to handle document viewing with signed URLs
const ReportDocumentLink = ({ url, name }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      let filePath = '';
      if (url.includes('/storage/v1/object/public/documents/')) {
        filePath = url.split('/storage/v1/object/public/documents/')[1];
      } else if (url.includes('/storage/v1/object/sign/documents/')) {
        filePath = url
          .split('/storage/v1/object/sign/documents/')[1]
          .split('?')[0];
      } else {
        window.open(url, '_blank');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error('Failed to open document');
        setLoading(false);
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error opening document:', err);
      toast.error('Failed to open document');
    }
    setLoading(false);
  };

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{ cursor: loading ? 'wait' : 'pointer' }}
    >
      {loading ? (
        <Spinner size="sm" animation="border" />
      ) : (
        <FontAwesomeIcon icon="link" />
      )}{' '}
      {name}
    </a>
  );
};

ReportDocumentLink.propTypes = {
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
};

const Hospital = () => {
  const { id } = useParams();

  const hospitalColumns = [
    {
      accessor: 'hospital_name',
      Header: 'Hospital Name',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { hospital } = rowData.row.original;
        return <>{hospital?.name || '-'}</>;
      }
    },
    {
      accessor: 'hospital_address',
      Header: 'Location',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { hospital } = rowData.row.original;
        return <>{hospital?.address || '-'}</>;
      }
    },
    {
      accessor: 'start_date',
      Header: 'Start Date',
      Cell: rowData => {
        const { start_date } = rowData.row.original;
        return start_date ? format(new Date(start_date), 'dd/MM/yyyy') : '-';
      }
    },
    {
      accessor: 'end_date',
      Header: 'End Date',
      Cell: rowData => {
        const { end_date } = rowData.row.original;
        return end_date ? format(new Date(end_date), 'dd/MM/yyyy') : 'Present';
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            isNotEdit
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.facilities.children.hospital}
          />
        );
      }
    }
  ];

  const reportColumns = [
    {
      accessor: 'hosptial',
      Header: 'Hospital',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'inspection_date',
      Header: 'Inspection Date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { inspection_date } = rowData.row.original;
        return inspection_date
          ? dayjs(inspection_date).format('DD/MM/YYYY')
          : '-';
      }
    },
    {
      accessor: 'review_date',
      Header: 'Review Date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { review_date } = rowData.row.original;
        return review_date ? dayjs(review_date).format('DD/MM/YYYY') : '-';
      }
    },
    {
      accessor: 'medical_note',
      Header: 'Notes',
      cellProps: {
        className: 'py-2',
        style: { maxWidth: '200px' }
      },
      Cell: rowData => {
        const { medical_note } = rowData.row.original;
        return (
          <span
            style={{
              display: 'block',
              maxWidth: '200px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={medical_note || ''}
          >
            {medical_note || '-'}
          </span>
        );
      }
    },
    {
      accessor: 'medical_document',
      Header: 'Report File',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { medical_document } = rowData.row.original;
        // medical_document is stored as jsonb, might be an array or object
        const docs = Array.isArray(medical_document)
          ? medical_document
          : medical_document
          ? [medical_document]
          : [];

        if (docs.length === 0) return 'No File';

        const firstDoc = docs[0];
        const url = typeof firstDoc === 'string' ? firstDoc : firstDoc?.url;
        const name =
          typeof firstDoc === 'string' ? 'Report' : firstDoc?.name || 'Report';

        return url ? <ReportDocumentLink url={url} name={name} /> : 'No File';
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            isNotEdit
            onCLickDelete={() => handleDeleteReportClick(id)}
            subject={permissionsKeyMap.sponsorship.children.beneficiaries}
          />
        );
      }
    }
  ];

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteReportModal, setShowDeleteReportModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [addHospitalModalShow, setAddHospitalModalShow] = useState(false);
  const [addReportModalShow, setAddReportModalShow] = useState(false);
  const { uploadDocument, loading: isUploadReportLoading } = useUpload();

  const { organization, selectedBranchId, user } = useAuthContext();

  // Use hooks for hospitals
  const {
    beneficiaryHospitals,
    isLoading: loading,
    refetch: refetchHospitals
  } = useBeneficiaryHospitals(id);
  const { createHospitalOrphan, isCreating: isAddHospitalLoading } =
    useCreateHospitalOrphan();
  const { deleteHospitalOrphan } = useDeleteHospitalOrphan();

  // Use hooks for medical reports
  const {
    medicalReports,
    isLoading: reportsLoading,
    refetch: refetchReports
  } = useMedicalReports(id);
  const { createMedicalReport, isCreating: isAddReportLoading } =
    useCreateMedicalReport();

  // Get all hospitals for the dropdown
  const { hospitals } = useHospitals();

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteReportClick = id => {
    setSelectedId(id);
    setShowDeleteReportModal(true);
  };

  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const handleDeleteReportModalHide = () => {
    setSelectedId(null);
    setShowDeleteReportModal(false);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    deleteHospitalOrphan(selectedId);
    refetchHospitals();
  };

  const handleConfirmDeleteReport = async () => {
    setShowDeleteReportModal(false);
    // TODO: Add delete medical report hook when available
    toast.info('Delete functionality coming soon');
    refetchReports();
  };

  const submitAddHospital = async values => {
    createHospitalOrphan({
      hospital_id: values.hospital,
      orphan_id: id,
      branch_id: selectedBranchId,
      organisation_id: organization.id,
      user_id: user.id,
      start_date: values.startDate === '' ? null : values.startDate,
      end_date: values.endDate === '' ? null : values.endDate
    });
    setAddHospitalModalShow(false);
  };

  const submitAddReport = async values => {
    const reportFile = values.reportDocument?.[0];
    let medical_document = [];

    if (reportFile && reportFile instanceof File) {
      try {
        const url = await uploadDocument(reportFile, 'medical-reports');
        medical_document = [{ name: reportFile.name, url }];
      } catch (err) {
        return;
      }
    }

    createMedicalReport({
      branch_id: selectedBranchId,
      organisation_id: organization.id,
      orphan_id: id,
      user_id: user.id,
      hosptial: values.hospital,
      doctor_name: values.doctorName || '',
      inspection_date: values.inspectionDate,
      review_date: values.reviewDate,
      medical_note: values.comment || '',
      medical_document
    });
    setAddReportModalShow(false);
    refetchReports();
  };

  const hospitalOptions = useMemo(() => {
    return (
      hospitals?.map(item => ({
        label: item.name,
        value: item.id
      })) || []
    );
  }, [hospitals]);

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal || showDeleteReportModal}
        onHide={
          showDeleteModal ? handleDeleteModalHide : handleDeleteReportModalHide
        }
        title={showDeleteModal ? 'Remove Hospital' : 'Delete Report'}
        handleConfirm={
          showDeleteModal ? handleConfirmDelete : handleConfirmDeleteReport
        }
        body={
          showDeleteModal
            ? 'Are you sure you want to remove selected Hospital?'
            : 'Are you sure you want to remove selected Report?'
        }
      />

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={beneficiaryHospitals || []}
            columns={hospitalColumns}
            title="Hospitals"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddHospitalModalShow(true);
            }}
            loading={loading}
            addNew
            subject={permissionsKeyMap.facilities.children.hospital}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={medicalReports || []}
            columns={reportColumns}
            title="Medical Reports"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddReportModalShow(true);
            }}
            loading={reportsLoading}
            addNew
            subject={permissionsKeyMap.facilities.children.hospital}
          />
        </Col>
      </Row>

      {addHospitalModalShow && (
        <AddHospitalModal
          show={addHospitalModalShow}
          onSubmit={submitAddHospital}
          isSubmitLoading={isAddHospitalLoading}
          onHide={() => {
            setAddHospitalModalShow(false);
          }}
          title="Add Hospital"
          hospitalOptions={hospitalOptions}
        />
      )}

      {addReportModalShow && (
        <AddReportModal
          show={addReportModalShow}
          onHide={() => {
            setAddReportModalShow(false);
          }}
          title="Add Medical Report"
          loading={isAddReportLoading || isUploadReportLoading}
          onSubmit={submitAddReport}
          hospitalOptions={hospitalOptions}
        />
      )}
    </>
  );
};

export default Hospital;
